/* eslint-disable react/no-unescaped-entities */
import { useRef, useState } from "react";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import {
  media, rooms, banquetImages, destinations, services, IconGlyph,
  trekkingImg, jungleExpImg, fishingExpImg,
  restaurantImg, atriumReceptionImg, doubleHeightLoungeImg, theVandaBuildingImg,
  vandaHeroImg, orchidPatternImg,
} from "@/data";
import { useBooking } from "@/context/BookingContext";
import BookingFields from "@/components/BookingFields";
import barHomeImg from "@/assets/hotel/bar-home.png";

export default function Home() {
  const { openBooking } = useBooking();
  const [roomIndex, setRoomIndex] = useState(0);
  const regionTrackRef = useRef(null);

  const scrollRegions = (dir) => {
    const track = regionTrackRef.current;
    if (!track) return;
    const card = track.querySelector(".region-card");
    const step = card ? card.getBoundingClientRect().width + 24 : 320;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <>
      {/* HERO — split: copy left, booking right */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-bg" style={{ backgroundImage: `url(${vandaHeroImg})` }} aria-hidden="true" />
        <div className="hero-scrim" aria-hidden="true" />
        <div className="hero-grid">
          <div className="hero-left fade-up">
            <h1 data-testid="hero-heading">
              A Boutique Hotel in the Heart of <em>Tura</em>
            </h1>
            <p className="hero-sub">Every modern comfort. Set right in Tura.</p>
            <a href="#book-now" onClick={openBooking} className="btn btn-primary" data-testid="hero-book-now">
              Book Your Stay <ArrowRight size={16} />
            </a>
          </div>

          <aside className="booking-card fade-up" data-testid="booking-panel">
            <div className="booking-card-head">
              <h3>Booking Online</h3>
              <p>Check availability in seconds.</p>
            </div>
            <BookingFields onSubmit={openBooking} idPrefix="hero" />
          </aside>
        </div>
        <div className="hero-scroll">
          <span>Scroll to explore</span>
          <span className="scroll-line" />
        </div>
      </section>

      {/* SECTION 1 — VANDA EXPERIENCE (image left, text right) */}
      <section className="vanda-experience" id="about" data-testid="about-section">
        <div className="ve-inner">
          <div className="ve-image fade-up">
            <img src={theVandaBuildingImg} alt="The Vanda hotel building in Tura" />
            <div className="badge">
              <p className="badge-lbl">Rooted in Tura</p>
            </div>
          </div>

          <div className="ve-copy fade-up">
            <p className="eyebrow">
              <ArrowRight size={13} /> Every Moment Finds Its Place
            </p>
            <p className="script-title">THE VANDA EXPERIENCE</p>
            <h2>
              Some places are visited. <em>Others are experienced.</em>
            </h2>
            <p className="lede">At The Vanda, your day isn't planned by a schedule — it unfolds naturally.</p>

            <ul className="ve-list">
              <li>
                <span className="tick" />
                <p>Wake up to quiet mornings, framed by hill-town light.</p>
              </li>
              <li>
                <span className="tick" />
                <p>Savour slow breakfasts prepared with care at The Orchid Terrace.</p>
              </li>
              <li>
                <span className="tick" />
                <p>Step out to Tura, or step in for the quiet of a boutique stay.</p>
              </li>
            </ul>

            <a href="/about" className="btn btn-outline" data-testid="about-more-link">
              More about us <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 2 — OUR HOTEL: forest centered band with two side images (PDF copy) */}
      <section className="our-hotel-v2" id="about-more" data-testid="our-hotel-section">
        <div className="ohv2-side">
          <img src={atriumReceptionImg} alt="Atrium reception at The Vanda" />
        </div>
        <div className="ohv2-center fade-up">
          <p className="eyebrow light center">
            <ArrowRight size={13} /> Our Hotel
          </p>
          <h2>
            Welcome to a <em>Boutique Stay in Tura</em>
          </h2>
          <p className="ohv2-lede">
            The Vanda is Tura's finest — a new benchmark in business and leisure travel. Located in the heart of town, our hotel brings together the comfort of a boutique stay with everything today's guest needs. With well-appointed rooms, a fully equipped gym, a rejuvenating spa, a swimming pool, our restaurant The Orchid Terrace, dedicated banquet and conference facilities, and everything else you need for a seamless stay — whether you're here for work or to explore Tura.
          </p>
          <span className="ohv2-hline" aria-hidden="true" />
          <div className="ohv2-stats">
            <div className="ohv2-stat">
              <p className="ohv2-num word">Central</p>
              <p className="ohv2-lbl">Heart of Tura</p>
            </div>
            <a href="#rooms" className="ohv2-cta" data-testid="hotel-explore-link">
              Explore Our Rooms <ArrowUpRight size={16} />
            </a>
            <div className="ohv2-stat">
              <p className="ohv2-num word">Personal</p>
              <p className="ohv2-lbl">Thoughtful Service</p>
            </div>
          </div>
          <svg className="ohv2-curve" viewBox="0 0 300 60" aria-hidden="true">
            <path d="M0,60 C75,-20 225,-20 300,60" fill="none" stroke="rgba(255,253,248,0.16)" strokeWidth="1" />
          </svg>
        </div>
        <div className="ohv2-side">
          <img src={doubleHeightLoungeImg} alt="Double height lounge at The Vanda" />
        </div>
      </section>

      {/* SECTION 3 — Accommodations inspire banner */}
      <section className="rooms-banner" data-testid="rooms-banner-section">
        <div className="rooms-banner-head fade-up">
          <p className="eyebrow center">
            <ArrowRight size={13} /> Accommodations Inspire
          </p>
          <h3 className="banner-title">Your Stay Starts Here</h3>
        </div>
        <div className="rooms-strip">
          {rooms.map((r) => (
            <a key={r.name} href="/rooms" className="rooms-strip-card">
              <img src={r.img} alt={`${r.name} room at The Vanda`} loading="lazy" />
              <div>
                <p className="strip-tag">{r.tag}</p>
                <p className="strip-name">{r.name}</p>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* SECTION 4 — ROOMS & SUITES: 2-col heading + split image / iconbox list */}
      <section className="rooms" id="rooms" data-testid="rooms-section">
        <div className="rooms-head">
          <div className="rooms-head-left fade-up">
            <p className="eyebrow">
              <ArrowRight size={13} /> Rooms &amp; Suites
            </p>
            <h2>
              Choose <em>Your Room.</em>
            </h2>
            <p className="rooms-head-sub">Five distinct room categories — each a private sanctuary.</p>
          </div>
          <div className="rooms-head-right">
            <img src={rooms.find((r) => r.name === "Junior Suite").img} alt="Junior Suite room at The Vanda" className="fade-up" />
          </div>
        </div>

        <div className="rooms-split">
          <div className="rooms-split-image">
            <img src={rooms[roomIndex].img} alt={`${rooms[roomIndex].name} room`} key={roomIndex} />
            <div className="rooms-split-tag">
              <span>{String(roomIndex + 1).padStart(2, "0")}</span>
              <p>{rooms[roomIndex].tag}</p>
            </div>
          </div>

          <div className="rooms-split-copy fade-up">
            <p className="lede">
              Every room at The Vanda is a considered stay — natural textures, warm materials, generous windows, and calming interiors that feel effortlessly comfortable. Each one is named for the character it carries.
            </p>

            <ul className="rooms-list" role="tablist" aria-label="Room categories">
              {rooms.map((r, i) => (
                <li key={r.name}>
                  <button
                    role="tab"
                    aria-selected={roomIndex === i}
                    className={`room-row ${roomIndex === i ? "active" : ""}`}
                    onClick={() => setRoomIndex(i)}
                    data-testid={`room-tab-${i + 1}`}
                  >
                    <span className="room-tick" />
                    <div>
                      <h4>{r.name}</h4>
                      <p>{r.desc}</p>
                    </div>
                    <ArrowUpRight size={16} />
                  </button>
                </li>
              ))}
            </ul>

            <a href="/rooms" className="btn btn-primary" data-testid="rooms-explore-btn">
              Explore Rooms &amp; Suites <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 5 — DINING: forest bg with mountain ambient + pool image + cream card (PDF copy) */}
      <section className="dining-v2" id="dining" data-testid="dining-section">
        <div className="dv2-wash" aria-hidden="true" />
        <div className="dv2-header fade-up">
          <p className="eyebrow light">
            <ArrowRight size={13} /> Dining
          </p>
          <h2 className="dv2-overtitle">
            The Orchid <em>Terrace.</em>
          </h2>
        </div>
        <div className="dv2-inner">
          <div className="dv2-image fade-up">
            <img src={restaurantImg} alt="The Orchid Terrace restaurant interior" />
          </div>
          <div className="dv2-card fade-up">
            <div className="dv2-icon" aria-hidden="true">
              <svg viewBox="0 0 32 32" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M10 3v11" />
                <path d="M6 3v6a4 4 0 004 4" />
                <path d="M14 3v6a4 4 0 01-4 4" />
                <path d="M10 13v16" />
                <path d="M22 3c-2 2-3 5-3 9 0 4 1 6 3 6v11" />
              </svg>
            </div>
            <h3 className="dv2-card-heading">
              A space to sit back and <em>enjoy good food.</em>
            </h3>
            <p>
              The Orchid Terrace, our in-house restaurant, serves thoughtfully prepared meals through the day. From a quick breakfast to a relaxed dinner after a day out in Tura, it's a space to sit back and enjoy good food.
            </p>
            <a href="/dining" className="btn-forest" data-testid="dining-enquire">
              Explore Dining <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 6 — Activity: Trek to Tura Peak (copy left / image right) */}
      <section className="feature-split" id="activities" data-testid="activity-trek">
        <div className="feature-copy fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> Experiences Around Tura
          </p>
          <h2>
            Trek to <em>Tura Peak.</em>
          </h2>
          <p>A full-day trek to Tura Peak, overlooking the beautiful town of Tura.</p>
          <button onClick={openBooking} className="arrow-btn" aria-label="Plan the Tura Peak trek" data-testid="trek-cta">
            <ArrowRight size={20} />
          </button>
          <span className="rotor" aria-hidden="true" />
        </div>
        <div className="feature-image">
          <img src={trekkingImg} alt="Trekking a mountain trail near Tura" />
        </div>
      </section>

      {/* SECTION 7 — Activity: A Day in the Jungle (image left / copy right) */}
      <section className="feature-split reversed dark-bg" data-testid="activity-jungle">
        <div className="feature-image">
          <img src={jungleExpImg} alt="Jungle river near Tura" />
        </div>
        <div className="feature-copy fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> Experiences Around Tura
          </p>
          <h2>
            A Day in the Jungle, <em>by the River.</em>
          </h2>
          <p>Spend a day out in the jungle, by the riverside, cooking on wood — where the river water is your deck, and the outdoors is your restaurant. Experience how life feels, away from concrete.</p>
          <button onClick={openBooking} className="arrow-btn" aria-label="Plan a day in the jungle" data-testid="jungle-cta">
            <ArrowRight size={20} />
          </button>
          <span className="rotor" aria-hidden="true" />
        </div>
      </section>

      {/* SECTION 8 — Activity: Waters That Remember (copy left / image right) */}
      <section className="feature-split" data-testid="activity-fishing">
        <div className="feature-copy fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> Experiences Around Tura
          </p>
          <h2>
            Experience <em>Fishing.</em>
          </h2>
          <p>Something modern city life has left behind — fishing was once one of the main means of living in the early days of civilisation. Experience it for yourself.</p>
          <button onClick={openBooking} className="arrow-btn" aria-label="Plan a fishing day" data-testid="fishing-cta">
            <ArrowRight size={20} />
          </button>
          <span className="rotor" aria-hidden="true" />
        </div>
        <div className="feature-image">
          <img src={fishingExpImg} alt="Fishing on a mountain river near Tura" />
        </div>
      </section>

      {/* SECTION 9 — Explore the Region */}
      <section className="region" data-testid="region-section" style={{ backgroundImage: `linear-gradient(rgba(33,32,32,0.82), rgba(33,32,32,0.82)), url(${orchidPatternImg})` }}>
        <div className="region-head fade-up">
          <div>
            <p className="eyebrow light">
              <ArrowRight size={13} /> Explore the Region
            </p>
            <h2>
              Explore <em>Tura.</em>
            </h2>
          </div>
          <div className="region-head-right">
            <p className="region-sub">Step outside, and Tura's landscape is right at your doorstep.</p>
            <div className="carousel-controls">
              <button onClick={() => scrollRegions(-1)} aria-label="Previous" data-testid="region-prev">
                <ChevronLeft />
              </button>
              <button onClick={() => scrollRegions(1)} aria-label="Next" data-testid="region-next">
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>
        <div className="region-grid" ref={regionTrackRef}>
          {destinations.map((d, i) => (
            <a href="/experiences" key={d.name} className="region-card" data-testid={`region-card-${i + 1}`}>
              <img src={d.img} alt={d.name} loading="lazy" />
              <span className="region-card-pill">0{i + 1}</span>
              <span className="region-card-body">
                <strong>{d.name}</strong>
                <em>{d.tag}</em>
              </span>
              <span className="region-card-arrow"><ArrowUpRight size={17} /></span>
            </a>
          ))}
        </div>
      </section>

      {/* SECTION 10 — Enhancing Your Stay: DARK bg, 2-col + 2x2 icon grid */}
      <section className="amenities-v2" id="amenities-section" data-testid="amenities-section">
        <div className="av2-inner">
          <div className="av2-title fade-up">
            <p className="eyebrow light">
              <ArrowRight size={13} /> Services
            </p>
            <h2>
              Enhancing Your Stay <em>With Exclusive Amenities.</em>
            </h2>
            <div className="av2-phone">
              <span className="av2-phone-icon">
                <Phone size={22} />
              </span>
              <div>
                <p className="av2-phone-lbl">For More Information</p>
                <p className="av2-phone-num">Contact details to be added</p>
              </div>
            </div>
          </div>

          <div className="av2-grid">
            {[
              { name: "Transportation", desc: "Airport transfers and local travel arranged whenever you need them.", icon: "plane", highlight: true },
              { name: "Wi-Fi and Internet", desc: "Complimentary high-speed Wi-Fi across every corner of the property.", icon: "wifi" },
              { name: "Fitness", desc: "A fully-equipped fitness studio open around the clock.", icon: "dumbbell" },
              { name: "Room Services", desc: "Attentive, in-room service throughout your stay.", icon: "bed" },
            ].map((a) => (
              <div className={`av2-card fade-up ${a.highlight ? "highlight" : ""}`} key={a.name}>
                <span className="av2-card-icon">
                  <IconGlyph name={a.icon} />
                </span>
                <div>
                  <h4>{a.name}</h4>
                  <p>{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Signature amenities — just 3, shown as a static grid, no carousel needed */}
        <div className="service-carousel" data-testid="service-carousel">
          <div className="service-carousel-head fade-up">
            <p className="eyebrow light">
              <ArrowRight size={13} /> Signature Amenities
            </p>
          </div>
          <div className="service-track">
            {services.map((s) => (
              <article className="service-card" key={s.title}>
                <img src={s.img} alt={s.title} />
                <div className="service-card-body">
                  <p className="service-card-tag">Amenity</p>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 11 — Banquet & Conference (single PDF-matched block, images loop-flip between banquet hall / conference hall) */}
      <section className="banquet" data-testid="banquet-section">
        <div className="banquet-image">
          {banquetImages.map((b, i) => (
            <img key={b.alt} src={b.src} alt={b.alt} className={`bq-card bq-card-${i + 1}`} />
          ))}
        </div>
        <div className="banquet-copy fade-up">
          <p className="eyebrow light">
            <ArrowRight size={13} /> Banquet &amp; Conference
          </p>
          <h2>
            Spaces for <em>Every Occasion.</em>
          </h2>
          <p>From business meetings and conferences to weddings and celebrations, our Grand Banquet Hall and Conference Room are equipped to host up to 500 guests — with seating for every format, from boardroom to banquet.</p>
          <p className="banquet-capacity">
            Up to <strong>500</strong> guests
          </p>
          <button onClick={openBooking} className="btn btn-primary" data-testid="banquet-enquire">
            Enquire Now <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* SECTION 11.5 — The Vanda Bar, full-bleed hero-style banner */}
      <section className="home-bar" style={{ backgroundImage: `url(${barHomeImg})` }} data-testid="home-bar-section">
        <div className="home-bar-copy fade-up">
          <p className="home-bar-eyebrow">
            The Vanda Bar <span className="home-bar-line" aria-hidden="true" />
          </p>
          <h2>
            Good Drinks.<br />Great Company.
          </h2>
          <p>
            Unwind with handcrafted cocktails, premium spirits and a curated selection of wines — perched above Tura, with the Garo Hills as your backdrop. The perfect space to end the day, or begin the evening.
          </p>
          <a href="/dining" className="btn btn-outline light" data-testid="home-bar-cta">
            Explore Our Bar <ArrowRight size={15} />
          </a>
        </div>
      </section>

      {/* SECTION 12 — Booking CTA band */}
      <section className="cta-band light" id="book-now" data-testid="cta-band">
        <p className="eyebrow center">
          <ArrowRight size={13} /> Your next chapter
        </p>
        <h2>
          A Boutique Hotel <em>in the Heart of Tura.</em>
        </h2>
        <button onClick={openBooking} className="btn btn-primary large" data-testid="cta-book-now">
          Book Your Stay <ArrowRight size={17} />
        </button>
      </section>
    </>
  );
}
